/**
 * CrisisPromptContent — Initial crisis support prompt UI
 *
 * Extracted from CrisisPractitionerPrompt.tsx as part of E2 refactoring, S2.4 (#742).
 * Renders the initial "do you want to reach out?" view with practitioner availability check.
 */

import { View, Text, Pressable, ActivityIndicator } from 'react-native';
import { styles, type TFunction } from '../styles/crisis-practitioner.styles';

export interface PromptContentProps {
  t: TFunction;
  hasPractitioners?: boolean;
  loadingPractitioners: boolean;
  onRequestSupport: () => void;
  onClose: () => void;
}

export function PromptContent({
  t,
  hasPractitioners,
  loadingPractitioners,
  onRequestSupport,
  onClose,
}: PromptContentProps) {
  return (
    <View style={styles.contentContainer}>
      {/* Header */}
      <View style={styles.header}>
        <Text style={styles.headerIcon}>🤝</Text>
        <Text style={styles.headerTitle}>
          {t('practitioner_prompt.title')}
        </Text>
        <Text style={styles.headerSubtitle}>
          {t('practitioner_prompt.subtitle')}
        </Text>
      </View>

      {/* Description */}
      <View style={styles.descriptionSection}>
        <Text style={styles.description}>
          {t('practitioner_prompt.description')}
        </Text>
        <View style={styles.privacyNotice}>
          <Text style={styles.privacyNoticeIcon}>🔒</Text>
          <Text style={styles.privacyNoticeText}>
            {t('practitioner_prompt.privacy_notice')}
          </Text>
        </View>
      </View>

      {/* Loading state */}
      {loadingPractitioners && (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#6366F1" />
        </View>
      )}

      {/* No practitioners available */}
      {!loadingPractitioners && !hasPractitioners && (
        <View style={styles.noPractitionersContainer}>
          <Text style={styles.noPractitionersTitle}>
            {t('practitioner_selector.no_practitioners')}
          </Text>
          <Text style={styles.noPractitionersDescription}>
            {t('practitioner_selector.no_practitioners_description')}
          </Text>
        </View>
      )}

      {/* Action Buttons */}
      <View style={styles.buttonContainer}>
        {hasPractitioners && !loadingPractitioners && (
          <Pressable
            onPress={onRequestSupport}
            style={styles.primaryButton}
            accessibilityRole="button"
            accessibilityLabel={t('practitioner_prompt.accessibility_label')}
            accessibilityHint={t('practitioner_prompt.accessibility_hint')}
            testID="crisis-practitioner-request"
          >
            <Text style={styles.primaryButtonText}>
              {t('practitioner_prompt.request_support')}
            </Text>
          </Pressable>
        )}

        <Pressable
          onPress={onClose}
          style={styles.secondaryButton}
          accessibilityRole="button"
          testID="crisis-practitioner-not-now"
        >
          <Text style={styles.secondaryButtonText}>
            {t('practitioner_prompt.not_now')}
          </Text>
        </Pressable>
      </View>
    </View>
  );
}
